import {
  Flex,
  Grid,
  Label,
  Loader,
  SelectField,
  TextField,
} from "@aws-amplify/ui-react";
import LocalizedStrings from "react-localization";
import { AddCircleOutline, DeleteOutline } from "@mui/icons-material";

import { operators } from "config/constants";
import { globalStrings } from "config/translations/es";
import { CCard, CCardBody } from "@coreui/react";

const ConditionForm = ({
  title,
  values,
  onChange,
  variables,
  isLoadingVariables,
}) => {
  const addInput = () => {
    onChange([
      ...values,
      {
        andOr: "",
        firstParam: "",
        time: "",
        timeValue: "",
        grouper: "",
        operator: "",
        secondParam: "",
      },
    ]);
  };

  const removeInput = (index) => {
    const listUpdated = [...values];
    listUpdated.splice(index, 1);
    onChange(listUpdated);
  };

  const onChangeInput = (index, name, value) => {
    const listUpdated = [...values];
    let valueMapped = value;

    if (name === "timeValue" && valueMapped <= 0) valueMapped = 1;

    listUpdated[index] = {
      ...listUpdated[index],
      [name]: valueMapped,
    };
    onChange(listUpdated);
  };

  return (
    <>
      <Flex alignItems="center">
        <div className="fs-5">{title}</div>
        <div style={styles.iconAction} onClick={addInput}>
          <AddCircleOutline color="action" />
        </div>
      </Flex>
      {values?.length > 0 && (
        <Grid paddingLeft="10px" gap="15px">
          {values?.map((item, index) => (
            <CCard className="p-2">
              <CCardBody>
                <Grid
                  key={index}
                  templateColumns={{ base: "1fr 10fr 1fr" }}
                  rowGap="15px"
                  columnGap="15px"
                >
                  <div>
                    {index !== 0 && (
                      <SelectField
                        label="AND/OR"
                        value={item.andOr}
                        onChange={(event) =>
                          onChangeInput(index, "andOr", event.target.value)
                        }
                      >
                        <option value="AND">AND</option>
                        <option value="OR">OR</option>
                      </SelectField>
                    )}
                  </div>
                  <Grid
                    key={index}
                    templateColumns={{ base: "1fr", large: "1fr 1fr 1fr" }}
                    rowGap="15px"
                    columnGap="15px"
                    alignItems="center"
                  >
                    {!!isLoadingVariables ? (
                      <Loader />
                    ) : (
                      <SelectField
                        label={strings.fields.variable.label}
                        value={item.firstParam}
                        onChange={(event) =>
                          onChangeInput(index, "firstParam", event.target.value)
                        }
                      >
                        <option value="">
                          {strings.fields.variable.placeholder}
                        </option>
                        {variables?.map((variable) => (
                          <option value={variable.id}>{variable.name}</option>
                        ))}
                      </SelectField>
                    )}
                    <Grid>
                      <Label>{strings.fields.timeValue.label}</Label>
                      <Flex gap="5px" alignItems="center">
                        <TextField
                          width="100%"
                          placeholder={strings.fields.timeValue.placeholder}
                          type="number"
                          value={item.timeValue ?? ""}
                          min={1}
                          onChange={(event) =>
                            onChangeInput(
                              index,
                              "timeValue",
                              event.target.value
                            )
                          }
                        ></TextField>
                      </Flex>
                    </Grid>
                    <SelectField
                      label={strings.fields.time.label}
                      value={item.time}
                      onChange={(event) =>
                        onChangeInput(index, "time", event.target.value)
                      }
                    >
                      <option value="">
                        {strings.fields.time.placeholder}
                      </option>
                      {Object.entries(strings.fields.timeValue.options).map(
                        (opt) => (
                          <option value={opt[0]}>{opt[1]}</option>
                        )
                      )}
                    </SelectField>
                    <SelectField
                      label={strings.fields.grouper.label}
                      value={item.grouper}
                      onChange={(event) =>
                        onChangeInput(index, "grouper", event.target.value)
                      }
                    >
                      <option value="">
                        {strings.fields.grouper.placeholder}
                      </option>
                      {Object.entries(strings.fields.grouper.options)
                        .filter((opt) =>
                          variables?.find(
                            (variable) => variable.id === item.firstParam
                          )?.groupType === "1"
                            ? opt[0] !== "sum"
                            : opt[0] === "sum"
                        )
                        .map((gr) => (
                          <option value={gr[0]}>{gr[1]}</option>
                        ))}
                    </SelectField>
                    <SelectField
                      label={strings.fields.operator.label}
                      value={item.operator}
                      onChange={(event) =>
                        onChangeInput(index, "operator", event.target.value)
                      }
                    >
                      <option value="">
                        {strings.fields.operator.placeholder}
                      </option>
                      {operators.map((op) => (
                        <option value={op}>
                          {globalStrings.operators[op]}
                        </option>
                      ))}
                    </SelectField>
                    <Grid>
                      <Label>{strings.fields.value.label}</Label>
                      <Flex gap="5px" alignItems="center">
                        <TextField
                          width="100%"
                          placeholder={strings.fields.value.placeholder}
                          value={item.secondParam}
                          onChange={(event) =>
                            onChangeInput(
                              index,
                              "secondParam",
                              event.target.value
                            )
                          }
                        ></TextField>
                        {!!variables?.find(
                          (variable) => variable.id === item.firstParam
                        ) && (
                          <span>
                            {
                              variables?.find(
                                (variable) => variable.id === item.firstParam
                              )?.unit
                            }
                          </span>
                        )}
                      </Flex>
                    </Grid>
                  </Grid>
                  <div
                    style={styles.iconAction}
                    onClick={() => removeInput(index)}
                  >
                    <DeleteOutline color="error" />
                  </div>
                </Grid>
              </CCardBody>
            </CCard>
          ))}
        </Grid>
      )}
    </>
  );
};

const styles = {
  iconAction: {
    placeSelf: "center",
    cursor: "pointer",
  },
};

const strings = new LocalizedStrings({
  es: {
    fields: {
      variable: {
        label: "Variable",
        placeholder: "Seleccione variable",
      },
      time: {
        label: "Tiempo",
        placeholder: "Seleccione tiempo",
      },
      timeValue: {
        label: "De lo(a)s último(a)s",
        placeholder: "De lo(a)s último(a)s",
        options: {
          lastMinutes: "Minutos",
          lastHours: "Horas",
          lastDays: "Días",
          lastWeeks: "Semanas",
          lastMonths: "Meses",
        },
      },
      grouper: {
        label: "Agrupador",
        placeholder: "Seleccione agrupador",
        options: {
          sum: "Acumulado",
          avg: "Promedio",
          min: "Mínimo",
          max: "Máximo",
        },
      },
      operator: {
        label: "Operador",
        placeholder: "Seleccione operador",
      },
      value: {
        label: "Valor",
        placeholder: "Ingrese valor",
      },
    },
  },
});

export default ConditionForm;
